import { request } from './client';

// ── Tenants ─────────────────────────────────────────────────────────────────

export interface TenantSummary {
  id: string;
  displayName: string;
  status: 'active' | 'disabled' | string;
  vpHost: string;
  vpDatabase: string;
  createdUtc: string;
  msGraphConsented: boolean;
}

export interface TenantDetail {
  id: string;
  vpHost: string;
  vpDatabase: string;
  vpClientId: string;
  vpClientSecretSet: boolean;
  status: 'active' | 'disabled' | string;
  displayName: string;
  createdUtc: string;
  createdBy: string;
  msGraphAadTenantId: string | null;
  msGraphConsentedAt: string | null;
  agentEnabled: boolean;
}

export interface CreateTenantRequest {
  id: string;
  vpHost: string;
  vpDatabase: string;
  vpClientId: string;
  vpClientSecret?: string;
  displayName?: string;
}

export interface UpdateTenantRequest {
  vpHost?: string;
  vpDatabase?: string;
  vpClientId?: string;
  displayName?: string;
  status?: 'active' | 'disabled';
  agentEnabled?: boolean;
}

export interface AdminConsentUrlResponse {
  url: string;
  expiresAt: string;
}

export function listTenants(): Promise<TenantSummary[]> {
  return request<TenantSummary[]>('/api/admin/tenants');
}

export function getTenant(id: string): Promise<TenantDetail> {
  return request<TenantDetail>(`/api/admin/tenants/${encodeURIComponent(id)}`);
}

export function createTenant(payload: CreateTenantRequest): Promise<TenantDetail> {
  return request<TenantDetail>('/api/admin/tenants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function updateTenant(id: string, payload: UpdateTenantRequest): Promise<TenantDetail> {
  return request<TenantDetail>(`/api/admin/tenants/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function replaceVpClientSecret(id: string, vpClientSecret: string): Promise<void> {
  // 204 No Content — request<T> assumes a JSON body, so go via fetch directly.
  const res = await fetch(`/api/admin/tenants/${encodeURIComponent(id)}/vp-client-secret`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ vpClientSecret }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => undefined);
    throw new Error(body?.detail ?? body?.title ?? `${res.status} ${res.statusText}`);
  }
}

export function generateAdminConsentUrl(id: string): Promise<AdminConsentUrlResponse> {
  return request<AdminConsentUrlResponse>(
    `/api/admin/tenants/${encodeURIComponent(id)}/admin-consent`,
    { method: 'POST' },
  );
}

// ── Employee search (typeahead for super-admin add) ────────────────────────

export interface Employee {
  employee: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  email: string;
  employeeCompany: string;
  status: string;
}

export interface EmployeeSearchResponse {
  employees: Employee[];
  hasMore: boolean;
}

export function searchEmployees(q: string, page = 1): Promise<EmployeeSearchResponse> {
  return request<EmployeeSearchResponse>(
    `/api/admin/employees/search?q=${encodeURIComponent(q)}&page=${page}`);
}

// ── Super-admins ────────────────────────────────────────────────────────────

export interface SuperAdminEntry {
  vpTenantId: string;
  vpUserId: string;
  createdUtc: string;
  createdBy: string;
}

/**
 * Body for POST /api/admin/super-admins. Tenant is implicit — the server
 * always uses the caller's vpTenantId. See ADR-0023 Amendment A: same-tenant
 * only by design.
 */
export interface AddSuperAdminRequest {
  vpUserId: string;
}

export function listSuperAdmins(): Promise<SuperAdminEntry[]> {
  return request<SuperAdminEntry[]>('/api/admin/super-admins');
}

export function addSuperAdmin(payload: AddSuperAdminRequest): Promise<SuperAdminEntry> {
  return request<SuperAdminEntry>('/api/admin/super-admins', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function removeSuperAdmin(vpTenantId: string, vpUserId: string): Promise<void> {
  const res = await fetch(
    `/api/admin/super-admins/${encodeURIComponent(vpTenantId)}/${encodeURIComponent(vpUserId)}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => undefined);
    throw new Error(body?.detail ?? body?.title ?? `${res.status} ${res.statusText}`);
  }
}

// ── Tenant-admins (ADR-0040) ─────────────────────────────────────────────────
// Policy = tenant_admin OR super_admin. A non-super caller may only target
// their own vpTenantId — the backend 403s on cross-tenant requests.

export interface TenantAdminEntry {
  vpTenantId: string;
  vpUserId: string;
  createdUtc: string;
  createdBy: string;
}

export interface AddTenantAdminRequest {
  vpUserId: string;
}

export function listTenantAdmins(vpTenantId: string): Promise<TenantAdminEntry[]> {
  return request<TenantAdminEntry[]>(`/api/admin/tenant-admins/${encodeURIComponent(vpTenantId)}`);
}

export function addTenantAdmin(vpTenantId: string, payload: AddTenantAdminRequest): Promise<TenantAdminEntry> {
  return request<TenantAdminEntry>(`/api/admin/tenant-admins/${encodeURIComponent(vpTenantId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// Uses request() (not raw fetch) so a 403 cannot_remove_self / 404
// tenant_admin_not_found surfaces as ApiClientError.code for the caller to
// switch on — see Admin.vue's removeEditingTenantAdmin / removeYourAdmin.
export function removeTenantAdmin(vpTenantId: string, vpUserId: string): Promise<void> {
  return request<void>(
    `/api/admin/tenant-admins/${encodeURIComponent(vpTenantId)}/${encodeURIComponent(vpUserId)}`,
    { method: 'DELETE' },
  );
}

// The tenant's own Zoom connection (ADR-0048 §1), always the caller's tenant.
export function getZoomTenantConnection(): Promise<{ connected: boolean }> {
  return request<{ connected: boolean }>('/api/zoom/tenant');
}

export function disconnectZoomTenant(): Promise<void> {
  return request<void>('/api/zoom/tenant/disconnect', { method: 'POST' });
}
